import { createContext, FC, useCallback, useContext, useMemo, useRef, useState } from 'react';

import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';

export type A11yContextValue = {
  screenReaderAnnouncement: string;
  setScreenReaderAnnouncement: (announcement: string) => void;
};

export const A11yContext = createContext<A11yContextValue>({
  screenReaderAnnouncement: '',
  setScreenReaderAnnouncement: () => {},
});

export const A11yContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  // Two alternating polite live regions rather than one. A single live region does not reliably
  // re-announce text identical to what it already holds, so setting the same message twice — e.g.
  // the same validation error on a repeated submit — can be silent to assistive tech; a same-node
  // clear-then-reset was confirmed unreliable in screen-reader testing regardless of delay.
  // Alternating the message between two regions (and clearing the other as the new one receives it)
  // makes every announcement a fresh insertion into a just-emptied region — a more distinct
  // mutation for assistive tech to pick up. Empirical workaround; verify with real AT.
  const [regions, setRegions] = useState<[string, string]>(['', '']);
  const nextRegionRef = useRef<0 | 1>(0);

  const handleSetScreenReaderAnnouncement = useCallback((announcement: string) => {
    setTimeout(() => {
      if (!announcement) {
        setRegions(['', '']);
        return;
      }
      const target = nextRegionRef.current;
      nextRegionRef.current = target === 0 ? 1 : 0;
      setRegions(target === 0 ? [announcement, ''] : ['', announcement]);
    }, 0);
  }, []);

  const contextValueMemo = useMemo(
    () => ({
      screenReaderAnnouncement: regions[0] || regions[1],
      setScreenReaderAnnouncement: handleSetScreenReaderAnnouncement,
    }),
    [regions, handleSetScreenReaderAnnouncement]
  );

  return (
    <A11yContext.Provider value={contextValueMemo}>
      <A11yHiddenBox aria-live="polite" aria-atomic="true">
        {regions[0]}
      </A11yHiddenBox>
      <A11yHiddenBox aria-live="polite" aria-atomic="true">
        {regions[1]}
      </A11yHiddenBox>
      {children}
    </A11yContext.Provider>
  );
};

export const useA11yContext = (): A11yContextValue => useContext(A11yContext);
